import { ILogger } from "@tdp/common/telemetry";
import { AppListFetchResult, AppPackageDownloadResult, IAppListReader } from "../abstractions/IAppListReader";
import { ApiFetchStatus } from "@tdp/common/constants";
import { parseApiResponse } from "@tdp/core/util/parseApiResponse";
import { AppSummary, IAppSummary } from "../abstractions/IAppSummary";
import { z } from "zod";
import { IAppListItemDeleteResult, IAppListItemDelete } from "../abstractions/IAppListItemDelete";
import { IAppListItemWriter, IAppListItemWriterResult } from "../abstractions/IAppListItemWriter";

const appsListApi = {
  getAppsCount: () => { return `/appdefinitions/my/count`; },
  getMyAppsByPageNumber: (page: number) => { return `/appdefinitions/my?pageNumber=${page}`; },
  deleteAppById: (appId: string) => { return `/appdefinitions/${appId}`; },
  createNewApp: () => { return `/appdefinitions`; },
  duplicateApp: (appId: string, name: string) => { return `/appdefinitions/duplicate/${appId}/${name}`; },
  writeAppSummary: (appId: string) => { return `/appdefinitions/${appId}`; },
  downloadAppPackage: (appId: string) => { return `/appdefinitions/${appId}/manifest`; },
  importAppPackage: (overwriteIfAppAlreadyExists: boolean) => { return `/appdefinitions/v2/import?overwriteIfAppAlreadyExists=${overwriteIfAppAlreadyExists}`; },
  takeOwnership: (appId: string) => { return `/appdefinitions/${appId}/takeownership`; },
  firstPartyPublishingStatus: (appId: string) => { return `/appStatusTracking/apps/${appId}/status`; },
  firstPartyImportCatalog: (overwriteIfAppAlreadyExists: boolean = false) => { return `/appdefinitions/importAppDefinitionItemFromCatalog?overwriteIfAppExistsInDeveloperPortal=${overwriteIfAppAlreadyExists}`; },
};

export interface ITdpAppsListService {
  logger: ILogger;
  apiFetch: typeof fetch;
}

export class TdpAppListService implements IAppListReader, IAppListItemDelete, IAppListItemWriter {
  private logger: ILogger;
  private apiFetch: typeof fetch;

  constructor({ apiFetch, logger }: ITdpAppsListService) {
    this.apiFetch = apiFetch;
    this.logger = logger;
  }

  async readAppsCount(): Promise<number> {
    const response = await this.apiFetch(appsListApi.getAppsCount())
    const paresedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error getting apps count",
      logger: this.logger,
      zodType: z.number(),
    });
    this.logger.logUserBi({ telemetryMarker: "appsListService.readAppsCount" }) // For demonstration purposes only
    return paresedResponse.data ?? 0
  }

  async readAppList(n: number): Promise<AppListFetchResult> {
    const response = await this.apiFetch(appsListApi.getMyAppsByPageNumber(n));
    const paresedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error loading apps list",
      logger: this.logger,
      zodType: z.array(AppSummary),
    });
    return {
      type: ApiFetchStatus.SUCCESS,
      appsList: paresedResponse.data ?? [],
    };
  }

  async deleteApp(appId: string): Promise<IAppListItemDeleteResult> {
    const response = await this.apiFetch(appsListApi.deleteAppById(appId), {
      method: "DELETE",
    });
    const paresedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error loading apps list",
      logger: this.logger,
      zodType: z.boolean(),
    });
    if (paresedResponse.type === ApiFetchStatus.ERROR) {
      return {
        type: ApiFetchStatus.ERROR,
      };
    }
    return {
      type: ApiFetchStatus.SUCCESS,
      result: paresedResponse.data ?? false,
    };
  }

  async createApp(payload: Object): Promise<IAppListItemWriterResult> {
    const response = await this.apiFetch(appsListApi.createNewApp(), {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const paresedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error creating app",
      logger: this.logger,
      zodType: AppSummary,
    });
    if (paresedResponse.type === ApiFetchStatus.ERROR) {
      return {
        type: ApiFetchStatus.ERROR,
        errorMessage: paresedResponse.errorMessage,
      };
    }
    return {
      type: ApiFetchStatus.SUCCESS,
      response: paresedResponse.data as IAppSummary
    };
  }

    async duplicateApp(appId: string, shortName: string): Promise<IAppListItemWriterResult> {
    const response = await this.apiFetch(appsListApi.duplicateApp(appId, shortName), {
      method: "POST",
    });
    const paresedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error duplicating the app",
      logger: this.logger,
      zodType: AppSummary,
    });
    if (paresedResponse.type === ApiFetchStatus.ERROR) {
      return {
        type: ApiFetchStatus.ERROR,
        errorMessage: paresedResponse.errorMessage,
      };
    }
    return {
      type: ApiFetchStatus.SUCCESS,
      response: paresedResponse.data as IAppSummary
    };
  }

  async writeAppSummary(payload: Record<string, any>): Promise<IAppListItemWriterResult> {
    const response = await this.apiFetch(appsListApi.writeAppSummary(payload?.teamsAppId || ''), {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const paresedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error creating app",
      logger: this.logger,
      zodType: AppSummary,
    });
    if (paresedResponse.type === ApiFetchStatus.ERROR) {
      return {
        type: ApiFetchStatus.ERROR,
        errorMessage: paresedResponse.errorMessage,
      };
    }
    return {
      type: ApiFetchStatus.SUCCESS,
      response: paresedResponse.data as IAppSummary
    };
  }

  async downloadApp(appId: string): Promise<AppPackageDownloadResult> {
    const response = await this.apiFetch(appsListApi.downloadAppPackage(appId), {
      method: "GET",
    });
    const paresedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error downloading app",
      logger: this.logger,
      zodType: z.string(),
    });
    if (paresedResponse.type === ApiFetchStatus.ERROR) {
      return {
        type: ApiFetchStatus.ERROR,
        error: paresedResponse.errorMessage,
      };
    }
    return {
      type: ApiFetchStatus.SUCCESS,
      appPackage: paresedResponse.data as string,
    };
  }

  async importApp(overwriteIfAppAlreadyExists: boolean, file: File): Promise<IAppListItemWriterResult> {
    const response = await this.apiFetch(appsListApi.importAppPackage(overwriteIfAppAlreadyExists), {
      method: 'POST',
      headers: {
        "Content-Type": "application/zip; charset=utf-8"
      },
      body: file,
    });
    const paresedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error importing app",
      logger: this.logger,
      zodType: AppSummary,
    });
    if (paresedResponse.type === ApiFetchStatus.ERROR) {
      return {
        type: ApiFetchStatus.ERROR,
        errorMessage: paresedResponse.errorMessage,
      };
    }
    return {
      type: ApiFetchStatus.SUCCESS,
      response: paresedResponse.data as IAppSummary,
    };
  }

  async takeOwnership(appId: string): Promise<IAppListItemWriterResult> {
    const response = await this.apiFetch(appsListApi.takeOwnership(appId), {
      method: "POST",
    });
    const paresedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error taking ownership of app",
      logger: this.logger,
      zodType: AppSummary,
    });
    if (paresedResponse.type === ApiFetchStatus.ERROR) {
      return {
        type: ApiFetchStatus.ERROR,
        errorMessage: paresedResponse.errorMessage,
      };
    }
    return {
      type: ApiFetchStatus.SUCCESS,
      response: paresedResponse.data as IAppSummary
    };
  }

  async firstPartyPublish(appId: string): Promise<AppPackageDownloadResult> {
    const response = await this.apiFetch(appsListApi.firstPartyPublishingStatus(appId), {
      method: "GET",
    });
    const paresedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error getting first party publishing status",
      logger: this.logger,
      zodType: z.string(),
    });
    if (paresedResponse.type === ApiFetchStatus.SUCCESS) {
      return {
        type: ApiFetchStatus.SUCCESS,
        appPackage: paresedResponse.data as string,
      };
    }
    return {
      type: ApiFetchStatus.ERROR,
      error: paresedResponse.errorMessage,
    };
  }

  async firstPartyImportCatalog(payload: Record<string, string>): Promise<IAppListItemWriterResult> {
    const response = await this.apiFetch(appsListApi.firstPartyImportCatalog(), {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const paresedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error importing app from catalog",
      logger: this.logger,
      zodType: AppSummary,
    });
    if (paresedResponse.type === ApiFetchStatus.ERROR) {
      return {
        type: ApiFetchStatus.ERROR,
        errorMessage: paresedResponse.errorMessage,
      };
    }
    return {
      type: ApiFetchStatus.SUCCESS,
      response: paresedResponse.data as IAppSummary
    };
  }

}